import useWidth from '@components/Hooks/useWidth';
import Icon from '@components/v2/ui/Icon/Icon';
import { Box, InputBase, InputBaseComponentProps, Stack } from '@mui/material';
import { Search, X } from 'lucide-react';
import { RefObject } from 'react';
import { useSearchField } from '../../../../../../hooks/useSearchField';
import LangEnum from '../../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../../modules/translation/TranslationContext';
import { useSearchFieldStyles } from './searchField.styles';

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  onEnter?: () => void;
  inputRef?: RefObject<HTMLInputElement>;
  placeholder?: LangEnum;
  testId?: string;
  inputProps?: InputBaseComponentProps;
  hasOtherContent?: boolean;
}

function SearchField({
  value,
  onChange,
  onClear,
  onEnter,
  inputRef,
  placeholder,
  testId,
  inputProps,
  hasOtherContent = true,
}: SearchFieldProps) {
  const { handleChange, handleKeyDown, handleClearKeyDown } = useSearchField({
    onChange,
    onClear,
    onEnter,
  });
  const { translate } = useTranslation();
  const { smallWidth } = useWidth();

  const isCollapsed = smallWidth && !value && hasOtherContent;
  const translatedPlaceholder = !isCollapsed
    ? translate(placeholder ?? LangEnum.GLOBAL_SEARCH)
    : undefined;
  const { classes } = useSearchFieldStyles({ isCollapsed });

  return (
    <Stack direction="row" className={classes.root}>
      <Icon icon={Search} size="md" />
      <Box data-value={value || translatedPlaceholder || ''} className={classes.sizer}>
        <InputBase
          value={value}
          placeholder={translatedPlaceholder}
          inputRef={inputRef}
          className={classes.input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          inputProps={{
            maxLength: 120,
            enterKeyHint: 'search',
            ...inputProps,
            size: 1,
            'test-id': testId,
          }}
        />
      </Box>
      {value && (
        <Box
          role="button"
          tabIndex={0}
          component="span"
          aria-label={translate(LangEnum.GLOBAL_CLEAR)}
          className={classes.clearButton}
          onClick={onClear}
          onKeyDown={handleClearKeyDown}
        >
          <Icon icon={X} size="md" />
        </Box>
      )}
    </Stack>
  );
}

export type { SearchFieldProps };
export default SearchField;
